import type { BlogLocale } from "@/features/blog/locales";
import { cn } from "@/common/utils";
import { getAuthor } from "@/features/blog/authors";
import { BLOG_COPY, formatPostDate } from "@/features/blog/copy";

/** Author (photo, name linking to their profile), date and reading time. */
export function PostByline({
  authorName,
  publishedAt,
  readingMinutes,
  locale,
  className,
}: {
  authorName?: string;
  publishedAt: string;
  readingMinutes: number;
  locale: BlogLocale;
  className?: string;
}) {
  const copy = BLOG_COPY[locale];
  const author = getAuthor(authorName);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-2 text-[14px] text-(--g-ink-2)",
        className,
      )}
    >
      {authorName && (
        <span className="inline-flex basis-full items-center gap-2.5 whitespace-nowrap sm:basis-auto">
          {author && (
            <img
              src={author.image}
              alt=""
              width={28}
              height={28}
              className="size-7 rounded-full object-cover"
            />
          )}
          <span>
            {copy.by}{" "}
            {author ? (
              <a
                href={author.url}
                target="_blank"
                rel="noopener noreferrer author"
                className="text-(--g-ink) underline decoration-(--g-rule) underline-offset-[0.22em] transition-colors hover:decoration-current"
              >
                {authorName}
              </a>
            ) : (
              <span className="text-(--g-ink)">{authorName}</span>
            )}
          </span>
        </span>
      )}
      <span className="inline-flex items-center gap-3 whitespace-nowrap">
        {authorName && <Separator className="hidden sm:inline" />}
        <time dateTime={publishedAt}>
          {formatPostDate(locale, publishedAt)}
        </time>
      </span>
      <span className="inline-flex items-center gap-3 whitespace-nowrap">
        <Separator />
        {copy.minRead(readingMinutes)}
      </span>
    </div>
  );
}

/**
 * The dot between byline items; it stays on the line of the item after it. On
 * phones the author takes its own line, so the dot after it is dropped.
 */
function Separator({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cn("text-(--g-ink-3)", className)}>
      ·
    </span>
  );
}
